import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { apiService } from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";
import { useSearch } from "../../contexts/SearchContext";
import { InputIcon } from "primereact/inputicon";
import { IconField } from "primereact/iconfield";

export type Client = { id: number; name: string; external: boolean };

const ClientsAdmin: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const { searchTerm: filter, setSearchTerm: setFilter } = useSearch();
  const [modalVisible, setModalVisible] = useState(false);
  const [editClient, setEditClient] = useState<Client | null>(null);
  const [formName, setFormName] = useState("");
  const [formExternal, setFormExternal] = useState(false);
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    const res = await apiService.getClients();
    if (res.success) setClients(res.data);
    else toast.showError("Failed", res.message || "Unable to load clients");
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () =>
      clients.filter((c) =>
        c.name.toLowerCase().includes(filter.toLowerCase())
      ),
    [clients, filter]
  );

  const openNew = () => {
    setEditClient(null);
    setFormName("");
    setFormExternal(true);
    setModalVisible(true);
  };

  const openEdit = (c: Client) => {
    setEditClient(c);
    setFormName(c.name);
    setFormExternal(c.external);
    setModalVisible(true);
  };

  const save = async () => {
    if (!formName.trim()) {
      toast.showWarn("Validation", "Name is required");
      return;
    }
    const payload: Partial<Client> = {
      id: editClient?.id,
      name: formName.trim(),
      external: !!formExternal,
    };
    const res = await apiService.createOrUpdateClient(payload as any);
    if (res.success) {
      toast.showSuccess("Saved", `Client '${res.data.name}' saved`);
      setModalVisible(false);
      await load();
    } else {
      toast.showError("Error", res.message || "Save failed");
    }
  };

  const remove = async (c: Client) => {
    const res = await apiService.deleteClient(c.id);
    if (res.success) {
      toast.showSuccess("Deleted", `Client '${c.name}' deleted`);
      await load();
    } else {
      toast.showError("Error", res.message || "Delete failed");
    }
  };

  const header = (
    <div className="flex justify-content-between align-items-center">
      <div className="flex align-items-center gap-2">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search clients..."
          />
        </IconField>
      </div>
      <Button label="Add" icon="pi pi-plus" onClick={openNew} />
    </div>
  );

  return (
    <div>
      <DataTable
        value={filtered}
        loading={loading}
        header={header}
        emptyMessage="No clients found"
        className="p-datatable-sm"
        paginator
        rows={10}
      >
        <Column field="name" header="Name" sortable />
        <Column
          header="External"
          body={(row: Client) => (
            <i className={`pi ${row.external ? "pi-check" : "pi-times"}`} />
          )}
          sortable
        />
        <Column
          header="Actions"
          body={(row: Client) => (
            <div className="flex gap-2 justify-content-end">
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-outlined p-button-sm"
                onClick={() => openEdit(row)}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-outlined p-button-sm p-button-danger"
                onClick={() => remove(row)}
              />
            </div>
          )}
          style={{ width: "120px" }}
          headerStyle={{ textAlign: "right" }}
        />
      </DataTable>

      <Dialog
        header={editClient ? "Edit Client" : "Add Client"}
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        style={{ width: 420 }}
        modal
      >
        <div className="flex flex-column gap-3">
          <div className="field">
            <label className="block mb-2">Name</label>
            <InputText
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />
          </div>
          <div className="field flex align-items-center gap-2">
            <Checkbox
              inputId="client-external"
              checked={formExternal}
              onChange={(e) => setFormExternal(!!e.checked)}
            />
            <label htmlFor="client-external">External</label>
          </div>
          <div className="flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setModalVisible(false)}
            />
            <Button label="Save" icon="pi pi-check" onClick={save} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ClientsAdmin;
